import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { Plus, RefreshCcw, XCircle, PauseCircle, PlayCircle, Download } from 'lucide-react';
import { PERMISSIONS } from '@pms/shared';
import type { ApiError } from '@pms/shared';
import {
  subscriptionService,
  type SubscriptionListItem,
  type SubscriptionDetail,
} from '@/services/subscription.service';
import { departmentsService } from '@/services/departments.service';
import { PermissionGate } from '@/components/guards/PermissionGate';
import { DataTable, type Column } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date-picker';
import { DueDateBadge } from './components/DueDateBadge';
import { SubscriptionStatusBadge } from './components/SubscriptionStatusBadge';
import { CreateSubscriptionModal } from './components/CreateSubscriptionModal';
import { EditSubscriptionModal } from './components/EditSubscriptionModal';
import { SubscriptionDetailDrawer } from './components/SubscriptionDetailDrawer';
import { RenewDialog } from './components/RenewDialog';
import { CancelDialog } from './components/CancelDialog';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

export function SubscriptionListPage() {
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const limit = 15;

  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [statusId, setStatusId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [billingCycleId, setBillingCycleId] = useState('');
  const [dueTo, setDueTo] = useState('');

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setDebouncedQ(q); setPage(1); }, 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [q]);

  const [createOpen, setCreateOpen] = useState(false);
  const [editSub, setEditSub] = useState<SubscriptionDetail | null>(null);
  const [viewId, setViewId] = useState<string | null>(null);
  const [renewSub, setRenewSub] = useState<SubscriptionListItem | null>(null);
  const [cancelSub, setCancelSub] = useState<SubscriptionListItem | null>(null);

  const params = {
    page,
    limit,
    q: debouncedQ || undefined,
    status_id: statusId || undefined,
    category_id: categoryId || undefined,
    department_id: departmentId || undefined,
    billing_cycle_id: billingCycleId || undefined,
    due_to: dueTo || undefined,
  };

  const { data: listData, isLoading, isError } = useQuery({
    queryKey: ['subscriptions', 'list', params],
    queryFn: () => subscriptionService.list(params),
  });

  const { data: statuses = [] } = useQuery({ queryKey: ['subscriptions', 'statuses'], queryFn: () => subscriptionService.listStatuses() });
  const { data: categories = [] } = useQuery({ queryKey: ['subscriptions', 'categories'], queryFn: () => subscriptionService.listCategories() });
  const { data: billingCycles = [] } = useQuery({ queryKey: ['subscriptions', 'billing-cycles'], queryFn: () => subscriptionService.listBillingCycles() });
  const { data: deptsData } = useQuery({ queryKey: ['departments'], queryFn: () => departmentsService.list({ limit: 200 }) });

  const updateFilter = (setter: (v: string) => void) => (v: string) => { setter(v === '__all__' ? '' : v); setPage(1); };

  const pauseMut = useMutation({
    mutationFn: (id: string) => subscriptionService.pause(id),
    onSuccess: () => { toast.success('Paused'); void queryClient.invalidateQueries({ queryKey: ['subscriptions'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const resumeMut = useMutation({
    mutationFn: (id: string) => subscriptionService.resume(id),
    onSuccess: () => { toast.success('Resumed'); void queryClient.invalidateQueries({ queryKey: ['subscriptions'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const handleEdit = async (id: string) => {
    try {
      const detail = await subscriptionService.get(id);
      setEditSub(detail);
    } catch {
      toast.error('Failed to load subscription');
    }
  };

  const columns: Column<SubscriptionListItem>[] = [
    {
      key: 'name', header: 'Name',
      cell: (r) => (
        <div>
          <button type="button" onClick={() => setViewId(r.id)} className="font-medium text-left hover:text-primary transition-colors text-sm">
            {r.name}
          </button>
          {r.vendor_name && <p className="text-xs text-muted-foreground">{r.vendor_name}</p>}
        </div>
      ),
    },
    {
      key: 'category', header: 'Category',
      cell: (r) => r.category_name
        ? (
          <span className="inline-flex items-center gap-1.5 text-sm">
            {r.category_color && <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: r.category_color }} />}
            {r.category_name}
          </span>
        )
        : <span className="text-muted-foreground text-sm">—</span>,
    },
    {
      key: 'department', header: 'Department',
      cell: (r) => r.department_name ?? <span className="text-muted-foreground text-sm">—</span>,
    },
    {
      key: 'billing', header: 'Cycle',
      cell: (r) => <span className="text-sm">{r.billing_cycle_name}</span>,
    },
    {
      key: 'amount', header: 'Amount',
      cell: (r) => (
        <span className="font-medium text-sm">
          {r.currency_symbol}{Number(r.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      key: 'next_due', header: 'Next Due',
      cell: (r) => <DueDateBadge nextDueDate={r.next_due_date} reminderDays={r.reminder_days} />,
    },
    {
      key: 'auto', header: 'Auto',
      cell: (r) => r.auto_renewal
        ? <span className="inline-flex h-5 items-center rounded-full bg-emerald-100 px-2 text-[10px] font-semibold text-emerald-700">ON</span>
        : <span className="inline-flex h-5 items-center rounded-full bg-muted px-2 text-[10px] font-semibold text-muted-foreground">OFF</span>,
    },
    {
      key: 'status', header: 'Status',
      cell: (r) => <SubscriptionStatusBadge name={r.status_name} color={r.status_color} />,
    },
    {
      key: 'actions', header: '', className: 'text-right',
      cell: (r) => {
        const isPaused = r.status_name?.toLowerCase() === 'paused';
        const isCancelled = r.status_name?.toLowerCase() === 'cancelled';
        return (
          <div className="flex items-center justify-end gap-0.5">
            <PermissionGate permission={PERMISSIONS.SUBSCRIPTIONS_EDIT}>
              <button type="button" onClick={() => void handleEdit(r.id)} className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" title="Edit">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 13l6.586-6.586a2 2 0 112.828 2.828L11.828 15.83a4 4 0 01-1.897 1.05l-2.622.655.655-2.622A4 4 0 019 13z" /></svg>
              </button>
            </PermissionGate>
            <PermissionGate permission={PERMISSIONS.SUBSCRIPTIONS_RENEW}>
              <button type="button" onClick={() => setRenewSub(r)} disabled={isCancelled} className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-primary disabled:opacity-40 disabled:pointer-events-none" title="Renew">
                <RefreshCcw className="h-4 w-4" />
              </button>
            </PermissionGate>
            <PermissionGate permission={PERMISSIONS.SUBSCRIPTIONS_EDIT}>
              {isPaused ? (
                <button type="button" onClick={() => resumeMut.mutate(r.id)} className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-emerald-600" title="Resume">
                  <PlayCircle className="h-4 w-4" />
                </button>
              ) : !isCancelled ? (
                <button type="button" onClick={() => pauseMut.mutate(r.id)} className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-amber-600" title="Pause">
                  <PauseCircle className="h-4 w-4" />
                </button>
              ) : null}
            </PermissionGate>
            <PermissionGate permission={PERMISSIONS.SUBSCRIPTIONS_CANCEL}>
              <button type="button" onClick={() => setCancelSub(r)} disabled={isCancelled} className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-40 disabled:pointer-events-none" title="Cancel">
                <XCircle className="h-4 w-4" />
              </button>
            </PermissionGate>
          </div>
        );
      },
    },
  ];

  const pagination = listData ? {
    page: listData.pagination.page,
    totalPages: listData.pagination.totalPages,
    total: listData.pagination.total,
    onPageChange: (p: number) => setPage(p),
  } : undefined;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Subscriptions</h1>
          <p className="text-sm text-muted-foreground">Manage all organization subscriptions and renewals</p>
        </div>
        <div className="flex items-center gap-2">
          <PermissionGate permission={PERMISSIONS.SUBSCRIPTIONS_EXPORT}>
            <Button variant="outline" size="sm" onClick={() => void subscriptionService.exportCsv(params)}>
              <Download className="h-4 w-4 mr-1" /> Export
            </Button>
          </PermissionGate>
          <PermissionGate permission={PERMISSIONS.SUBSCRIPTIONS_CREATE}>
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4 mr-1" /> Add Subscription
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          value={q}
          onChange={setQ}
          placeholder="Search subscriptions…"
          containerClassName="min-w-[200px] flex-1 max-w-xs"
        />

        <Select value={statusId || '__all__'} onValueChange={updateFilter(setStatusId)}>
          <SelectTrigger className="h-9 w-40">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Statuses</SelectItem>
            {statuses.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                <span className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                  {s.name}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={categoryId || '__all__'} onValueChange={updateFilter(setCategoryId)}>
          <SelectTrigger className="h-9 w-44">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Categories</SelectItem>
            {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={departmentId || '__all__'} onValueChange={updateFilter(setDepartmentId)}>
          <SelectTrigger className="h-9 w-44">
            <SelectValue placeholder="All Departments" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Departments</SelectItem>
            {(deptsData?.data ?? []).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={billingCycleId || '__all__'} onValueChange={updateFilter(setBillingCycleId)}>
          <SelectTrigger className="h-9 w-40">
            <SelectValue placeholder="All Cycles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Cycles</SelectItem>
            {billingCycles.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-1.5 shrink-0">
          <span className="text-xs text-muted-foreground whitespace-nowrap">Due by</span>
          <DatePicker
            value={dueTo}
            onChange={(v) => { setDueTo(v); setPage(1); }}
            placeholder="Any date"
            className="h-9 w-36"
          />
        </div>

        {listData && (
          <span className="ml-auto text-xs text-muted-foreground">
            {listData.pagination.total} {listData.pagination.total === 1 ? 'subscription' : 'subscriptions'}
          </span>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <DataTable
          columns={columns}
          rows={listData?.data ?? []}
          rowKey={(r) => r.id}
          loading={isLoading}
          error={isError}
          onRetry={() => void queryClient.invalidateQueries({ queryKey: ['subscriptions', 'list'] })}
          empty="No subscriptions match the current filters"
          pagination={pagination}
        />
      </div>

      {/* Modals */}
      <CreateSubscriptionModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <EditSubscriptionModal open={!!editSub} subscription={editSub} onClose={() => setEditSub(null)} />
      <SubscriptionDetailDrawer
        subscriptionId={viewId}
        onClose={() => setViewId(null)}
        onEdit={(sub) => { setViewId(null); setEditSub(sub); }}
        onRenew={(sub) => { setViewId(null); setRenewSub(sub as unknown as SubscriptionListItem); }}
        onCancel={(sub) => { setViewId(null); setCancelSub(sub as unknown as SubscriptionListItem); }}
      />
      <RenewDialog subscription={renewSub} onClose={() => setRenewSub(null)} />
      <CancelDialog subscription={cancelSub} onClose={() => setCancelSub(null)} />
    </div>
  );
}
